import { createFileRoute, Link, notFound, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { ArrowLeft, Phone } from "lucide-react";
import { PROJECTS, type Project } from "@/data/portfolio";
import { PortfolioCard } from "@/components/site/PortfolioCard";
import { track } from "@/lib/analytics/track";

export const Route = createFileRoute("/gallery/$projectId")({
  loader: ({ params }) => {
    const project = PROJECTS.find((p) => p.id === params.projectId);
    if (!project) throw notFound();
    return { project };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Project not found — Michael's Painting" }, { name: "robots", content: "noindex" }] };
    }
    const { project } = loaderData;
    const title = `${project.title} · ${project.city} — Michael's Painting`;
    const desc = project.description;
    const url = `https://michaelspaintingwi.com/gallery/${project.id}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:image", content: project.image },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: project.image },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: ProjectDetailPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="min-h-[60vh] grid place-items-center bg-[#030303] text-white px-6 text-center">
        <div>
          <p className="text-red-300 mb-4">{error.message}</p>
          <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
        </div>
      </div>
    );
  },
  notFoundComponent: ProjectNotFound,
});

function ProjectNotFound() {
  return (
    <div className="min-h-[60vh] grid place-items-center bg-[#030303] text-white px-6 text-center">
      <div>
        <p className="text-[11px] tracking-[0.25em] uppercase text-amber-300 mb-3">Not found</p>
        <h1 className="font-serif text-3xl mb-4">We couldn't find that project.</h1>
        <Link to="/gallery" className="inline-flex items-center gap-2 bg-white text-zinc-950 rounded-full px-5 py-2 text-sm font-semibold hover:bg-amber-200">
          <ArrowLeft className="size-4" /> Back to gallery
        </Link>
      </div>
    </div>
  );
}

function ProjectDetailPage() {
  const { project } = Route.useLoaderData();

  useEffect(() => {
    track.funnel("gallery_to_lead", "project_view", 1, { project_id: project.id });
    try { window.sessionStorage.setItem("mp_from_gallery", "1"); } catch {}
  }, [project.id]);

  const related: Project[] = PROJECTS.filter(
    (p) => p.id !== project.id && (p.surface === project.surface || p.category === project.category),
  ).slice(0, 3);

  const trackCtaClick = () => {
    track.funnel("gallery_to_lead", "cta_click", 2, { project_id: project.id });
  };

  return (
    <>
      <section className="relative bg-[#030303] text-white">
        <div className="relative w-full h-[52vh] min-h-[380px] max-h-[720px] overflow-hidden">
          <img
            src={project.image}
            alt={project.title}
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-[#030303]/50 to-[#030303]/20" />
          <div className="absolute inset-x-0 bottom-0 pb-10 sm:pb-14">
            <div className="max-w-6xl mx-auto px-6">
              <Link
                to="/gallery"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-amber-200 hover:text-amber-100 mb-4"
              >
                <ArrowLeft className="size-3.5" /> Back to gallery
              </Link>
              <p className="text-[11px] uppercase tracking-[0.25em] text-amber-300 mb-3">
                {project.category} · {project.type} · {project.surface}
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
                {project.title}
              </h1>
              <p className="text-zinc-300 mt-3">{project.city}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#030303] text-white py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-[1.4fr,1fr] gap-12">
          <div className="space-y-6">
            <h2 className="font-serif text-2xl sm:text-3xl">About this project</h2>
            <p className="text-zinc-300 text-lg leading-relaxed">{project.description}</p>

            <dl className="grid grid-cols-2 gap-x-8 gap-y-5 pt-6 border-t border-white/10">
              <div>
                <dt className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Category</dt>
                <dd className="text-white">{project.category}</dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Client</dt>
                <dd className="text-white">{project.type}</dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Surface</dt>
                <dd className="text-white">{project.surface}</dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Location</dt>
                <dd className="text-white">{project.city}</dd>
              </div>
            </dl>
          </div>

          <aside className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 h-fit">
            <p className="text-[11px] uppercase tracking-[0.25em] text-amber-300 mb-3">Inspired by this?</p>
            <h3 className="font-serif text-2xl leading-tight mb-3">
              Get a free estimate for a project like this.
            </h3>
            <p className="text-sm text-zinc-400 mb-6">
              Share a few details and we'll scope a quote tailored to your space.
            </p>
            <div className="flex flex-col gap-3">
              <Link
                to="/contact"
                onClick={trackCtaClick}
                className="bg-white text-zinc-950 rounded-full px-6 py-3.5 text-sm font-semibold text-center hover:bg-amber-300 transition-colors"
              >
                Request an estimate
              </Link>
              <a
                href="tel:6088864598"
                onClick={trackCtaClick}
                className="inline-flex items-center justify-center gap-2 border border-white/15 text-white px-6 py-3.5 rounded-full text-sm font-semibold hover:bg-white/5 transition-colors"
              >
                <Phone className="size-4" /> (608) 886-4598
              </a>
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-brand-ivory py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="text-[11px] uppercase tracking-[0.25em] text-brand-navy/70 mb-2">More work</p>
                <h2 className="font-serif text-3xl text-brand-navy">Related projects</h2>
              </div>
              <Link to="/gallery" className="text-xs uppercase tracking-widest text-brand-navy hover:underline">
                View all
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((p) => (
                <PortfolioCard key={p.id} project={p} span="tall" />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}